/**
 * @description Shows browser alert before user closes, reloads, or leaves page
 *              with unsaved changes.
 */

export default {
  data() {
    return {
      /**
       * Whether a `beforeunload` listener has been added
       */
      isListening: false,

      /**
       * State to mark whether we should allow user to leave the page without a warning
       */
      hasUnsavedChanges: false,
    };
  },

  methods: {
    /**
     * triggers a confirmation prompt before browser tab is closed or reloaded
     */
    onLeaveEditor(e) {
      if (this.hasUnsavedChanges) {
        e.preventDefault(); // required for Firefox
        e.returnValue = ''; // required for Chrome

        return true;
      }

      return null;
    },

    /**
     * This method needs to be explicitly called within router components'
     * `beforeRouteLeave` navigation guard
     * @see https://router.vuejs.org/guide/migration/#navigation-guards-in-mixins-are-ignored
     *
     * @param next NavigationGuardNext
     */
    onBeforeRouteLeave(to, from, next) {
      if (!this.hasUnsavedChanges || to.redirectedFrom) {
        // Don't trigger confirmation on redirected route,
        // because it will create a redirect loop
        // when the route is mutated in other navigation guards (e.g. project plugin).
        next();
        return;
      }

      if (window.confirm('Leave editor? Changes you made may not be saved.')) {
        next();
      } else {
        next(false);
      }
    },

    addBeforeunloadListener() {
      if (!this.isListening) {
        window.addEventListener('beforeunload', this.onLeaveEditor);
        this.isListening = true;
      }
    },

    removeBeforeunloadListener() {
      if (this.isListening) {
        window.removeEventListener('beforeunload', this.onLeaveEditor);
        this.isListening = false;
      }
    },
  },

  watch: {
    /**
     * ! The `beforeunload` listener should only be added when a user has
     * unsaved changes, and removed as soon as that work has been saved.
     *
     * @see https://developer.chrome.com/blog/page-lifecycle-api/#the-beforeunload-event
     */
    hasUnsavedChanges(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (newVal) {
          this.addBeforeunloadListener();
        } else {
          this.removeBeforeunloadListener();
        }
      }
    },
  },

  beforeUnmount() {
    this.removeBeforeunloadListener();
  },
};
